import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { BASE_URL, notificationStyles } from "@/utils";
import NavigationBar from "@/components/NavigationBar";
import LoadingSpinner from "@/components/LoadingSpinner";
import StockLogo from "@/components/StockLogo";
import { FaDice, FaFilter, FaSearch, FaTimes } from "react-icons/fa";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import {
  readSimpleBotSetup,
  simpleBotSetupOwnerFromToken,
  updateSimpleBotSelection,
  updateSimpleBotSymbols,
} from "./simpleBotSetup";
import {
  sameScreeningPublication,
  screeningPublicationIdentity,
  stockCandidateCacheKey,
} from "./stockExplorerPublication";
import { pickAnotherLabel, stockFocusSelectionLineage } from "./botCreationPresentation";
import StockFocusCompanyDrawer from "./components/StockFocusCompanyDrawer";
import {
  stockCandidateSummary,
  stockClearSearchLabel,
  stockEmptySearchMessage,
  stockEmptyStateDetail,
  stockEmptyStateTitle,
  stockSearchRecoveryMessage,
  stockSearchPlaceholder,
  unambiguousUsTickerCandidate,
} from "./stockExplorerScopePresentation";

const stockCache = new Map();
const MAX_CACHE_ENTRIES = 5;
const CLIENT_PAGE_SIZE = 25;
const METRICS = [
  "market_cap", "pe_ratio", "pb_ratio", "dividend_yield", "roe", "roa",
  "gross_margin", "operating_margin", "ev_ebitda", "ps_ratio", "peg_ratio",
  "rsi", "atr_percent", "revenue_growth", "earnings_growth", "average_volume",
  "price_return_6m", "beta", "free_cash_flow", "current_ratio",
  "debt_to_equity", "price_to_free_cash_flow", "sector", "industry",
];
const NOT_PUBLISHED_METRICS = [
  "macd", "eps", "net_profit_margin", "book_value_per_share",
  "interest_coverage", "description", "domain", "last_updated",
];

const metricLabel = (metric) => metric === 'price_return_6m' ? '6 Month Price Return' : metric
  .replace(/_/g, " ")
  .replace(/\b\w/g, (character) => character.toUpperCase());

const genericDataLabel = (status) => ({
  CURRENT: 'Current published metrics',
  QUALITY_LIMITED: 'Current data did not pass quality checks',
  EXCLUDED: 'Outside the current supported stock set',
  LEGACY_UNVERIFIED: 'Older listing; current identity unverified',
  UNAVAILABLE: 'Current data unavailable',
}[status] || 'Current data unavailable');

const unavailableDetailReason = (status) => ({
  EXCLUDED: 'This listing is outside the supported stock set, so current stock detail is unavailable.',
  QUALITY_LIMITED: 'Current data did not pass quality checks, so stock detail is unavailable.',
}[status] || 'Current identity is unverified. Stock details will be available after review.');

const lastVerifiedPublicationDate = (stock) => {
  const value = stock?.availability?.last_verified_at;
  if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
  });
};

const restorePickResult = (session, symbols) => {
  if (!session || ![5, 10, 20].includes(Number(session.requestedCount))) return null;
  return {
    requestedCount: Number(session.requestedCount),
    selectedCount: symbols.length,
    stockFocusMatchCount: Number(session.stockFocusMatchCount)
      || Number(session.eligibleCount) || 0,
    eligibleCount: Number(session.eligibleCount) || 0,
    stockFocus: session.stockFocus || null,
    keptSymbols: Array.isArray(session.keptSymbols) ? session.keptSymbols : [],
    selectedCompanies: Array.isArray(session.selectedCompanies) ? session.selectedCompanies : [],
  };
};

const pickSessionPayload = (result) => (result ? {
  requestedCount: result.requestedCount,
  stockFocusMatchCount: result.stockFocusMatchCount,
  eligibleCount: result.eligibleCount,
  stockFocus: result.stockFocus,
  keptSymbols: result.keptSymbols,
  selectedCompanies: result.selectedCompanies,
} : null);

const orderedPickCompanies = (resultSymbols, previousSymbols, keptSymbols, requestedCount) => {
  const candidates = (Array.isArray(resultSymbols) ? resultSymbols : [])
    .map((item) => ({ symbol: String(item?.symbol || '').trim().toUpperCase(), company_name: item?.company_name || null, kept: item?.kept === true }))
    .filter((item) => /^[A-Z][A-Z0-9.-]{0,14}$/.test(item.symbol));
  const kept = new Map(candidates.filter((item) => item.kept).map((item) => [item.symbol, item]));
  const rolled = candidates.filter((item) => !item.kept);
  const visual = [];
  const used = new Set();
  let nextRolled = 0;
  previousSymbols.slice(0, requestedCount).forEach((symbol) => {
    if (keptSymbols.includes(symbol) && kept.has(symbol)) {
      visual.push(kept.get(symbol)); used.add(symbol);
    } else if (rolled[nextRolled]) {
      visual.push(rolled[nextRolled]); used.add(rolled[nextRolled].symbol); nextRolled += 1;
    }
  });
  candidates.forEach((item) => { if (!used.has(item.symbol)) visual.push(item); });
  return visual;
};

const StockExplorer = () => {
  const [stockData, setStockData] = useState([]);
  const [notification, setNotification] = useState(null);
  const [searchFeedback, setSearchFeedback] = useState('');
  const [pickFeedback, setPickFeedback] = useState(null);
  const [selectionFeedback, setSelectionFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchHint, setSearchHint] = useState(null);
  const [selectedMetrics, setSelectedMetrics] = useState({});
  const [page, setPage] = useState(1);
  const [resultPage, setResultPage] = useState(1);
  const [totalStocks, setTotalStocks] = useState(0);
  const [screeningUnavailable, setScreeningUnavailable] = useState(false);
  const [screeningGeneration, setScreeningGeneration] = useState(null);
  const [companyContextStock, setCompanyContextStock] = useState(null);
  const [openingInstrumentId, setOpeningInstrumentId] = useState(null);
  const [pickLoading, setPickLoading] = useState(false);
  const [pickResult, setPickResult] = useState(null);
  const [pickSettling, setPickSettling] = useState(false);
  const [pendingRollCount, setPendingRollCount] = useState(null);
  const pickUndo = useRef(null);
  const pickInFlight = useRef(false);
  const selectionFeedbackRef = useRef(null);
  const companyContextReturnFocus = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const practiceSetup = searchParams.get("setup") === "practice";
  const {
    token, isTokenVerified, verifyAndRefreshToken, axiosInstance,
  } = useContext(AuthContext);
  const simpleSetupOwner = simpleBotSetupOwnerFromToken(token);
  const [practiceSymbols, setPracticeSymbols] = useState(
    () => readSimpleBotSetup(simpleSetupOwner).symbols,
  );
  const practiceSymbolsRef = useRef(practiceSymbols);
  const practiceDraft = readSimpleBotSetup(simpleSetupOwner);
  const allStocksMode = practiceDraft.discoveryMode === 'ALL_STOCKS';
  const activeStockFocus = allStocksMode
    ? 'ALL_STOCKS'
    : String(practiceDraft.preset || '').trim();
  useEffect(() => {
    setPickResult((current) => current || restorePickResult(
      practiceDraft.pickSession, practiceDraft.symbols,
    ));
  // The setup draft is session-backed; only an owner change should hydrate this local UI state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simpleSetupOwner]);
  const [logos, setLogos] = useState({});
  const logoCache = useRef({});
  const adminSessionQuery = useQuery({
    queryKey: ["admin", "session"],
    queryFn: async () => (await axiosInstance.get("/api/admin/session")).data,
    enabled: Boolean(token && isTokenVerified && practiceSetup),
    retry: false,
    staleTime: 60000,
  });
  const developmentFixtureQuery = useQuery({
    queryKey: ["stock-screening", "development-fixture"],
    queryFn: async () => (
      await axiosInstance.get("/api/admin/stock-screening/development")
    ).data,
    enabled: practiceSetup && !allStocksMode && adminSessionQuery.data?.is_admin === true,
    retry: false,
    staleTime: 60000,
  });
  const developmentFixture = developmentFixtureQuery.data?.available === true
    ? developmentFixtureQuery.data
    : null;
  const useDevelopmentFixture = Boolean(practiceSetup && !allStocksMode && developmentFixture);

  useEffect(() => {
    if (!token || !isTokenVerified) {
      navigate("/login");
      return undefined;
    }
    if (practiceSetup && adminSessionQuery.isLoading) return undefined;
    if (
      practiceSetup
      && adminSessionQuery.data?.is_admin === true
      && developmentFixtureQuery.isLoading
    ) return undefined;
    const timeout = window.setTimeout(fetchStockData, searchQuery.trim() ? 350 : 0);
    return () => window.clearTimeout(timeout);
  }, [
    searchQuery, selectedMetrics, page, token, isTokenVerified, navigate,
    practiceSetup, adminSessionQuery.isLoading, adminSessionQuery.data?.is_admin,
    developmentFixtureQuery.isLoading,
    developmentFixture?.generation?.generation_id,
  ]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(stockData.length / CLIENT_PAGE_SIZE));
    setResultPage((current) => Math.min(current, pages));
  }, [stockData]);

  useEffect(() => {
    if (!selectionFeedback || !selectionFeedbackRef.current) return;
    const feedback = selectionFeedbackRef.current;
    const bounds = feedback.getBoundingClientRect();
    if (bounds.top < 0 || bounds.bottom > window.innerHeight) {
      feedback.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [selectionFeedback]);

  useEffect(() => {
    stockCache.clear();
    logoCache.current = {};
    setLogos({});
  }, [token]);

  const toggleMetric = (metric) => {
    setSelectedMetrics((prev) => ({
      ...prev,
      [metric]: !prev[metric],
    }));
  };

  const fetchLogos = useCallback(async (symbols) => {
    const validSymbols = symbols
      .filter((s) => s && typeof s === "string" && s.trim() !== "")
      .map((s) => s.toUpperCase());
    if (validSymbols.length === 0) return {};

    const newLogos = {};
    try {
      const symbolsToFetch = validSymbols.filter(
        (symbol) => !logoCache.current[symbol] || logoCache.current[symbol] === null
      );
      if (symbolsToFetch.length === 0) {
        return logoCache.current;
      }

      const response = await axios.post(
        `${BASE_URL}/api/stocks/logos/batch`,
        { symbols: symbolsToFetch },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );

      validSymbols.forEach((symbol) => {
        newLogos[symbol] = response.data[symbol] || logoCache.current[symbol] || null;
      });

      logoCache.current = { ...logoCache.current, ...newLogos };
    } catch {
      validSymbols.forEach((symbol) => {
        newLogos[symbol] = logoCache.current[symbol] || null;
      });
    }
    setLogos((prev) => ({ ...prev, ...logoCache.current }));
    return logoCache.current;
  }, [token]);

  const fetchStockData = async () => {
    setLoading(true);
    setSearchFeedback('');
    setSelectionFeedback(null);
    try {
      if (!isTokenVerified) {
        const isValid = await verifyAndRefreshToken();
        if (!isValid) {
          setNotification({ type: "error", message: "Session expired. Please log in again." });
          navigate("/login");
          return;
        }
      }

      const selected = Object.keys(selectedMetrics)
        .filter((key) => selectedMetrics[key])
        .map(String);
      const payload = {
        search: String(searchQuery || "").trim(),
        metrics: Array.isArray(selected) ? selected : [],
        page: page,
        ...(practiceSetup ? { practice_setup: true } : {}),
        ...(practiceSetup && allStocksMode ? { stock_selection_mode: 'ALL_STOCKS' } : {}),
        ...(practiceSetup && !allStocksMode && practiceDraft.preset ? { stock_focus: practiceDraft.preset } : {}),
        ...(!practiceSetup ? {
          include_generation: true,
          available_only: !searchQuery.trim(),
        } : {}),
      };
      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      };

      const countResponse = await axios.get(
          useDevelopmentFixture
            ? `${BASE_URL}/api/admin/stock-screening/development/count`
            : `${BASE_URL}/api/stocks/count`,
          {
            headers,
            timeout: 10000,
            params: practiceSetup && allStocksMode
              ? { stock_selection_mode: 'ALL_STOCKS' }
              : practiceSetup && practiceDraft.preset
                ? { stock_focus: practiceDraft.preset }
              : !practiceSetup ? { available_only: !searchQuery.trim() } : undefined,
          },
        );
      const countResult = countResponse.data;
      if (!screeningPublicationIdentity(countResult?.generation)) {
        throw new Error("Stock screening count lineage is incomplete");
      }
      const cacheKey = stockCandidateCacheKey({
        generation: countResult?.generation || null,
        token,
        searchQuery,
        selected,
        page,
        stockFocus: practiceSetup ? (allStocksMode ? 'ALL_STOCKS' : practiceDraft.preset) : null,
        developmentGeneration: useDevelopmentFixture
          ? developmentFixture.generation?.generation_id
          : null,
      });
      const cachedEntry = stockCache.get(cacheKey);
      if (cachedEntry) {
        const { stocks, total, generation, hint } = cachedEntry;
        setStockData(stocks);
        setTotalStocks(total);
        setScreeningUnavailable(false);
        setScreeningGeneration(generation || null);
        setSearchHint(hint || null);
        setNotification(null);
        await fetchLogos(stocks.filter((stock) => stock.data_status !== 'LEGACY_UNVERIFIED').map((stock) => stock.symbol));
        return;
      }

      const exploreEndpoint = useDevelopmentFixture
        ? `${BASE_URL}/api/admin/stock-screening/development/explore`
        : `${BASE_URL}/api/stocks/explore`;
      let response = await axios.post(
        exploreEndpoint,
        payload,
        { headers, timeout: 15000 }
      );
      let responseStocks = Array.isArray(response.data?.stocks) ? response.data.stocks : null;
      if (!responseStocks) {
        throw new Error("Unexpected stock screening response");
      }
      let generation = response.data.generation;
      if (
        !sameScreeningPublication(countResult?.generation, generation)
      ) {
        throw new Error("Stock screening publication changed while loading candidates");
      }
      let resolvedSearchHint = null;
      const tickerCandidate = responseStocks.length === 0
        ? unambiguousUsTickerCandidate(searchQuery)
        : null;
      if (tickerCandidate) {
        response = await axios.post(
          exploreEndpoint,
          { ...payload, search: tickerCandidate, page: 1 },
          { headers, timeout: 15000 },
        );
        const fallbackStocks = Array.isArray(response.data?.stocks) ? response.data.stocks : [];
        const fallbackGeneration = response.data?.generation || null;
        if (
          !sameScreeningPublication(countResult?.generation, fallbackGeneration)
        ) {
          throw new Error("Stock screening publication changed while checking the ticker hint");
        }
        const exactMatches = fallbackStocks.filter(
          (stock) => String(stock?.symbol || '').trim().toUpperCase() === tickerCandidate,
        );
        if (exactMatches.length === 1) {
          responseStocks = exactMatches;
          generation = fallbackGeneration;
          resolvedSearchHint = { query: searchQuery.trim(), symbol: tickerCandidate };
        }
      }
      setScreeningUnavailable(false);
      setScreeningGeneration(generation || null);
      setSearchHint(resolvedSearchHint);

      let resolvedTotalStocks = Number(countResult?.count || 0);

      if (responseStocks.length === 0 && searchQuery.trim()) {
        setStockData([]);
        setNotification(null);
        setTotalStocks(resolvedTotalStocks);
      } else {
        setStockData(responseStocks);
        setNotification(null);
        await fetchLogos(responseStocks.filter((stock) => stock.data_status !== 'LEGACY_UNVERIFIED').map((stock) => stock.symbol));
        if (!practiceSetup && searchQuery.trim()) {
          resolvedTotalStocks = responseStocks.length;
          setTotalStocks(resolvedTotalStocks);
        } else {
          setTotalStocks(resolvedTotalStocks);
        }
        stockCache.set(cacheKey, {
          stocks: responseStocks,
          total: resolvedTotalStocks,
          generation,
          hint: resolvedSearchHint,
        });
        if (stockCache.size > MAX_CACHE_ENTRIES) {
          const firstKey = stockCache.keys().next().value;
          stockCache.delete(firstKey);
        }
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const screeningIsUnavailable = !useDevelopmentFixture && (
        !practiceSetup
        || (error?.response?.status === 503
          && ["screening_data_unavailable", "supported_universe_unavailable"].includes(detail?.code))
      );
      const preservePriorResults = practiceSetup && !screeningIsUnavailable && stockData.length > 0;
      setScreeningUnavailable(screeningIsUnavailable || !preservePriorResults);
      setScreeningGeneration(null);
      setSearchHint(null);
      setNotification(null);
      if (!screeningIsUnavailable) setSearchFeedback(preservePriorResults
        ? 'Could not load these stocks. Showing your last loaded results; retry the search before selecting or picking stocks.'
        : 'Could not load these stocks. Check your search and try again.');
      if (!preservePriorResults) setStockData([]);
    } finally {
      setLoading(false);
    }
  };

  const togglePracticeSymbol = (symbol) => {
    const normalized = String(symbol || '').trim().toUpperCase();
    setNotification(null);
    setSelectionFeedback(null);
    const candidate = stockData.find((stock) => (
      String(stock?.symbol || '').trim().toUpperCase() === normalized
    ));
    if (
      practiceSetup
      && candidate?.practice_bot_eligible === false
      && !practiceSymbolsRef.current.includes(normalized)
    ) {
      setSelectionFeedback({
        symbol: normalized,
        type: 'warning',
        message: `${normalized} cannot currently be selected for a Practice bot. ${candidate.practice_bot_eligibility_message || 'Choose another compatible stock.'}`,
      });
      return;
    }
    const currentPublication = allStocksMode ? null : screeningPublicationIdentity(screeningGeneration);
    const storedSetup = readSimpleBotSetup(simpleSetupOwner);
    const storedLineage = storedSetup.screeningLineage;
    const selectionUsesCurrentPublication = Boolean(
      currentPublication
      && storedLineage
      && storedLineage.profileId === practiceDraft.preset
      && storedLineage.generationId === currentPublication.generationId
      && storedLineage.generationHash === currentPublication.generationHash
      && storedLineage.profileSchemaVersion === currentPublication.profileSchemaVersion
      && storedLineage.profileBundleHash === currentPublication.profileBundleHash
    );
    const mustStartExactSelection = Boolean(
      currentPublication
      && practiceSymbolsRef.current.length
      && !selectionUsesCurrentPublication
      && !useDevelopmentFixture
    );
    const current = mustStartExactSelection ? [] : practiceSymbolsRef.current;
    const next = current.includes(normalized)
      ? current.filter((item) => item !== normalized)
      : [...current, normalized];
    const activePick = mustStartExactSelection ? null : (pickResult || restorePickResult(
      storedSetup.pickSession, current,
    ));
    const nextPick = activePick ? {
      ...activePick,
      selectedCount: next.length,
      keptSymbols: Array.from(new Set([
        ...(activePick.keptSymbols || []).filter((symbol) => next.includes(symbol)),
        ...(!current.includes(normalized) ? [normalized] : []),
      ])),
      selectedCompanies: next.map((symbol) => (
        (activePick.selectedCompanies || []).find((item) => item.symbol === symbol)
        || { symbol, company_name: null, kept: !current.includes(normalized) && symbol === normalized }
      )),
    } : null;
    practiceSymbolsRef.current = next;
    setPracticeSymbols(next);
    if (currentPublication && !useDevelopmentFixture) {
      updateSimpleBotSelection(next, {
        ...currentPublication,
        profileId: practiceDraft.preset,
      }, simpleSetupOwner, undefined, mustStartExactSelection ? null : storedSetup.selectionMethod,
      nextPick ? pickSessionPayload(nextPick) : null);
    } else {
      updateSimpleBotSymbols(next, simpleSetupOwner);
    }
    setSelectionFeedback(mustStartExactSelection ? {
      symbol: normalized,
      type: 'warning',
      message: `${normalized} selected from the current published list. Earlier manual choices from another list were cleared so Pinngro can verify this selection.`,
    } : activePick && !current.includes(normalized) ? {
      symbol: normalized,
      type: 'success',
      message: 'Added manually · Kept for future picks.',
    } : null);
    setPickResult(nextPick);
  };

  const pickForMe = async (count) => {
    if (!practiceSetup || allStocksMode || !practiceDraft.preset || pickInFlight.current) return;
    const keptSymbols = (pickResult?.keptSymbols || []).filter((symbol) => practiceSymbolsRef.current.includes(symbol));
    if (keptSymbols.length > count) {
      setPickFeedback({ type: 'error', message: `You’ve kept ${keptSymbols.length} stocks. Unkeep or remove enough companies before choosing Pick ${count}.` });
      return;
    }
    pickInFlight.current = true;
    setPickLoading(true);
    setPickFeedback(null);
    try {
      const response = await axiosInstance.post('/api/stocks/stock-focus/pick', {
        stock_focus: practiceDraft.preset,
        count,
        kept_symbols: keptSymbols,
      });
      const result = response.data;
      const selectedCompanies = orderedPickCompanies(
        result?.symbols, practiceSymbolsRef.current, keptSymbols, Number(result?.requested_count),
      );
      const symbols = selectedCompanies.map((item) => item.symbol);
      const lineage = stockFocusSelectionLineage(result?.generation, result?.stock_focus);
      if (
        result?.stock_focus !== practiceDraft.preset
        || !lineage
        || symbols.length !== Number(result?.selected_count)
        || Number(result?.eligible_count) < symbols.length
        || !Number.isInteger(Number(result?.stock_focus_match_count))
        || Number(result?.stock_focus_match_count) < Number(result?.eligible_count)
      ) {
        throw new Error('The Stock Focus candidate list changed. Pick again or review your stocks before creating the bot.');
      }
      pickUndo.current = {
        symbols: practiceSymbolsRef.current,
        lineage: readSimpleBotSetup(simpleSetupOwner).screeningLineage,
        selectionMethod: readSimpleBotSetup(simpleSetupOwner).selectionMethod,
        pickResult,
      };
      practiceSymbolsRef.current = symbols;
      setPracticeSymbols(symbols);
      updateSimpleBotSelection(
        symbols, lineage, simpleSetupOwner, undefined, 'RANDOM_STOCK_FOCUS', {
          requestedCount: Number(result.requested_count),
          stockFocusMatchCount: Number(result.stock_focus_match_count),
          eligibleCount: Number(result.eligible_count),
          stockFocus: result.stock_focus,
          keptSymbols: selectedCompanies.filter((item) => item.kept).map((item) => item.symbol),
          selectedCompanies,
        },
      );
      setScreeningGeneration(result.generation);
      setPickResult({
        requestedCount: Number(result.requested_count),
        selectedCount: symbols.length,
        stockFocusMatchCount: Number(result.stock_focus_match_count),
        eligibleCount: Number(result.eligible_count),
        stockFocus: result.stock_focus,
        keptSymbols: selectedCompanies.filter((item) => item.kept).map((item) => item.symbol),
        selectedCompanies,
      });
      setPendingRollCount(null);
      setPickSettling(true);
      window.setTimeout(() => setPickSettling(false), 400);
      setNotification(null);
    } catch (error) {
      setPickFeedback({ type: 'error', message: error?.response?.data?.detail?.message
        || error?.message
        || 'Pinngro could not pick from the current Stock Focus candidates. Your selection was not changed.' });
    } finally {
      pickInFlight.current = false;
      setPickLoading(false);
    }
  };

  const undoPick = () => {
    const previous = pickUndo.current;
    if (!previous) return;
    practiceSymbolsRef.current = previous.symbols;
    setPracticeSymbols(previous.symbols);
    if (previous.lineage) {
      updateSimpleBotSelection(
        previous.symbols, previous.lineage, simpleSetupOwner, undefined, previous.selectionMethod,
        pickSessionPayload(previous.pickResult),
      );
    } else {
      updateSimpleBotSymbols(previous.symbols, simpleSetupOwner);
    }
    pickUndo.current = null;
    setPickResult(previous.pickResult || null);
    setPendingRollCount(null);
    setPickFeedback({ type: 'success', message: 'Your previous stock selection has been restored.' });
  };

  const requestRoll = (count) => {
    const wouldReplaceManualSelection = Boolean(practiceSymbolsRef.current.length)
      && !pickResult;
    if (wouldReplaceManualSelection) {
      setPendingRollCount(count);
      return;
    }
    pickForMe(count);
  };

  const togglePickedKeep = (symbol) => {
    const normalized = String(symbol || '').trim().toUpperCase();
    setPickResult((current) => {
      if (!current) return current;
      const kept = new Set(current.keptSymbols || []);
      if (kept.has(normalized)) kept.delete(normalized); else kept.add(normalized);
      const next = { ...current, keptSymbols: Array.from(kept) };
      const draft = readSimpleBotSetup(simpleSetupOwner);
      updateSimpleBotSelection(
        practiceSymbolsRef.current, draft.screeningLineage, simpleSetupOwner, undefined,
        draft.selectionMethod, pickSessionPayload(next),
      );
      return next;
    });
  };

  const pickKeptCount = (pickResult?.keptSymbols || []).filter((symbol) => practiceSymbols.includes(symbol)).length;
  const pickFinalCount = pickResult
    ? Math.min(Number(pickResult.requestedCount), Number(pickResult.eligibleCount))
    : 0;
  const pickRollLabel = pickResult
    ? pickAnotherLabel(Number(pickResult.requestedCount), pickKeptCount, pickFinalCount)
    : null;

  const openCompanyContext = (stock, trigger) => {
    companyContextReturnFocus.current = trigger;
    setCompanyContextStock(stock);
  };

  const openInstrument = (stock) => {
    const id = Number(stock?.instrument_id);
    if (!Number.isSafeInteger(id) || id <= 0) return;
    setOpeningInstrumentId(id);
    window.setTimeout(() => navigate(`/stock-explorer/instruments/${id}`), 0);
  };

  const closeCompanyContext = () => {
    setCompanyContextStock(null);
    window.setTimeout(() => companyContextReturnFocus.current?.focus(), 0);
  };

  const formatValue = (key, value, symbol, dataStatus) => {
    if (key === "logo") {
      return <StockLogo symbol={symbol} source={dataStatus === 'LEGACY_UNVERIFIED' ? null : logos[symbol]} loading={dataStatus !== 'LEGACY_UNVERIFIED' && logos[symbol] === undefined} />;
    }
    if (value === null || value === undefined) return "Unavailable";
    if (key === "market_cap") return `${(value / 1e9).toFixed(2)}B`;
    if (key === "average_volume") return value.toLocaleString();
    if (key === "free_cash_flow") return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (key === "price_return_6m") return `${value.toFixed(2)}%`;
    if (key === "last_updated") return new Date(value).toLocaleString();
    if (key === "domain") {
      return value ? (
        <a href={`https://${value}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
          {value}
        </a>
      ) : "N/A";
    }
    if (typeof value === "number") return value.toFixed(2);
    return value;
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setResultPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const preferredOrder = practiceSetup
    ? ["symbol", "company_name", "logo"]
    : ["logo", "company_name", "symbol"];
  const otherMetrics = Object.keys(selectedMetrics)
    .filter((key) => selectedMetrics[key] && !preferredOrder.includes(key))
    .sort();

  const selectedMetricCount = otherMetrics.length;
  const resultPages = Math.max(1, Math.ceil(stockData.length / CLIENT_PAGE_SIZE));
  const visibleStocks = stockData.slice(
    (resultPage - 1) * CLIENT_PAGE_SIZE,
    resultPage * CLIENT_PAGE_SIZE,
  );
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <NavigationBar />
      <main className="mx-auto w-full max-w-screen-2xl flex-1 px-4 pb-16 pt-24 sm:px-6 lg:px-8">
        {notification && !searchFeedback && (
          <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border p-4" style={notificationStyles[notification.type]} role="status">
            <span>{notification.message}</span>
            <button type="button" onClick={() => setNotification(null)} className="rounded-lg p-2 hover:bg-black/5" aria-label="Dismiss message">
              <FaTimes aria-hidden="true" />
            </button>
          </div>
        )}

        <header className="max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">{practiceSetup ? 'Bot setup' : 'Explore'}</p>
          <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl">{practiceSetup ? 'Choose your stocks' : 'Browse and search the market'}</h1>
          <p className="mt-3 text-lg leading-7 text-slate-600 dark:text-slate-300">
            {practiceSetup
              ? allStocksMode
                ? 'Search the full supported stock list and select only the companies you want this bot to watch.'
                : 'Pinngro has filtered the candidates using your Stock Focus. Now select the companies you want this bot to watch.'
              : 'Browse stocks with verified current data, or search for a specific ticker or company.'}
          </p>
        </header>

        {!practiceSetup && screeningGeneration?.technical_session_date && (
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
            Published stock data through {new Date(`${screeningGeneration.technical_session_date}T00:00:00`).toLocaleDateString()}.
          </p>
        )}

        {practiceSetup && (
          <section className="mt-6 flex flex-col gap-4 rounded-2xl border border-cyan-200 bg-cyan-50 p-5 text-cyan-950 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-black">Choosing stocks for your bot</p>
              <p className="mt-2 text-sm"><strong>Find stocks:</strong> {allStocksMode ? 'All Stocks' : practiceDraft.preset || 'Stock Focus not chosen'}</p>
              <p className="mt-1 text-sm"><strong>Trading Strategy:</strong> {practiceDraft.tradingStrategy ? practiceDraft.tradingStrategy.split('-').map((part) => part[0].toUpperCase() + part.slice(1)).join(' ') : 'Not chosen'}</p>
              <p className="mt-2 text-sm leading-6">{allStocksMode ? 'This is the full supported company universe; Pinngro has not applied a Stock Focus profile.' : 'Stock Focus narrows the market; you choose the actual stocks.'} The Trading Strategy does not filter this list, and selecting a stock does not mean it has generated a buy signal.</p>
              {screeningGeneration?.technical_session_date && (
                <p className="mt-2 text-xs text-cyan-800 dark:text-cyan-200">
                  Screening data as of {new Date(`${screeningGeneration.technical_session_date}T00:00:00`).toLocaleDateString()}.
                </p>
              )}
            </div>
            <Link to="/bots?step=3#hire" className="rounded-xl bg-cyan-700 px-5 py-3 text-center text-sm font-bold text-white hover:bg-cyan-800">
              Use selected stocks · Return to bot setup · {practiceSymbols.length || 0} selected
            </Link>
          </section>
        )}
        {useDevelopmentFixture && (
          <section className="mt-4 rounded-2xl border border-amber-400 bg-amber-50 p-4 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100" role="status">
            <p className="font-black">Development screening fixture</p>
            <p className="mt-1 text-sm leading-6">
              Operator-only test data from an older, incomplete legacy dataset. It is not current market screening data and cannot be activated as a production publication.
            </p>
            {developmentFixture.generation?.source_newest_row_timestamp && (
              <p className="mt-2 text-xs">
                Newest retained legacy row: {new Date(developmentFixture.generation.source_newest_row_timestamp).toLocaleString()}.
              </p>
            )}
          </section>
        )}

        {practiceSetup && !allStocksMode && !useDevelopmentFixture && (
          <section className="mt-6 rounded-3xl border border-blue-200 bg-blue-50 p-5 text-blue-950 shadow-sm dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100" aria-labelledby="pick-for-me-heading">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-700 dark:text-blue-300">Stock Focus helper</p>
                <h2 id="pick-for-me-heading" className="mt-1 flex items-center gap-2 text-2xl font-black"><FaDice aria-hidden="true" />Pick for me</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6">Don&apos;t know where to start? Every stock in this list currently matches the Stock Focus you chose. Pinngro can randomly choose a starting set for you. There is no hidden ranking or prediction.</p>
              </div>
              {totalStocks > 0 && <p className="shrink-0 rounded-full bg-white/70 px-3 py-1.5 text-sm font-black dark:bg-slate-950/50">{totalStocks} currently match</p>}
            </div>
            {screeningUnavailable ? (
              <p className="mt-4 rounded-xl bg-white/70 p-4 text-sm font-semibold dark:bg-slate-950/50">Current Stock Focus candidates are temporarily unavailable. Your selection will not change.</p>
            ) : pickResult?.eligibleCount === 0 ? (
              <p className="mt-4 rounded-xl bg-white/70 p-4 text-sm font-semibold dark:bg-slate-950/50">
                {pickResult.stockFocusMatchCount > 0
                  ? `${pickResult.stockFocusMatchCount} stocks currently match this Stock Focus, but none can currently be used in Practice. Browse the matches to review their Stock Focus evidence.`
                  : 'No stocks currently match this Stock Focus.'}
              </p>
            ) : totalStocks === 0 && !loading ? (
              <p className="mt-4 rounded-xl bg-white/70 p-4 text-sm font-semibold dark:bg-slate-950/50">No stocks currently match this Stock Focus.</p>
            ) : (
              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {[
                  { count: 5, label: 'Pick 5', supporting: 'Simple start' },
                  { count: 10, label: 'Pick 10', supporting: 'Broader selection' },
                  { count: 20, label: 'Pick 20', supporting: 'Broad selection' },
                ].map(({ count, label, supporting }, index) => (
                  <button key={count} type="button" onClick={() => requestRoll(count)} disabled={loading || pickLoading || screeningUnavailable || Boolean(searchFeedback) || totalStocks === 0} className={index === 0 ? 'min-h-12 rounded-xl bg-blue-700 px-4 py-3 text-left font-black text-white transition hover:bg-blue-800 disabled:cursor-wait disabled:opacity-60 motion-reduce:transition-none' : 'min-h-12 rounded-xl border border-blue-300 bg-white px-4 py-3 text-left font-black text-blue-800 transition hover:border-blue-500 hover:bg-blue-100 disabled:cursor-wait disabled:opacity-60 dark:border-blue-800 dark:bg-slate-950 dark:text-blue-200 dark:hover:bg-blue-950 motion-reduce:transition-none'}>
                    <span className="block">{pickLoading ? 'Picking…' : label}</span><span className="mt-0.5 block text-xs font-semibold opacity-80">{supporting}</span>
                  </button>
                ))}
              </div>
            )}
            {pickLoading && <p role="status" className="mt-3 text-sm font-semibold">Choosing stocks from this Stock Focus…</p>}
            {pickFeedback && <p role={pickFeedback.type === 'error' ? 'alert' : 'status'} className={`mt-3 rounded-xl border bg-white/70 p-3 text-sm font-semibold dark:bg-slate-950/50 ${pickFeedback.type === 'error' ? 'border-red-300 text-red-800 dark:text-red-200' : 'border-emerald-300 text-emerald-800 dark:text-emerald-200'}`}>{pickFeedback.message}</p>}
            {pickResult && pickResult.eligibleCount > 0 && <div className="mt-5 rounded-2xl bg-white/80 p-4 text-sm dark:bg-slate-950/50" role="status" aria-live="polite">
              <p className="font-black">{pickResult.selectedCount < pickResult.requestedCount ? `${pickResult.eligibleCount} of ${pickResult.stockFocusMatchCount} Stock Focus matches can currently be used in Practice, so I've selected all ${pickResult.selectedCount}.` : `${pickResult.selectedCount} random stocks selected from ${pickResult.eligibleCount} of ${pickResult.stockFocusMatchCount} Stock Focus matches that can currently be used in Practice.`}</p>
              <p className="mt-2 leading-6">{pickKeptCount} kept · {Math.max(pickFinalCount - pickKeptCount, 0)} will change. Each new pick is random from the same qualifying Stock Focus — no ranking or prediction involved.</p>
              <div className="mt-3 flex flex-wrap gap-3">{pickRollLabel ? <button type="button" onClick={() => requestRoll(pickResult.requestedCount)} disabled={pickLoading} className="min-h-11 font-bold text-blue-700 hover:underline disabled:opacity-60 dark:text-blue-300">{pickLoading ? 'Picking…' : pickRollLabel}</button> : <p className="inline-flex min-h-11 items-center font-bold text-emerald-700 dark:text-emerald-300">All {pickFinalCount} kept — your selection is ready.</p>}<button type="button" onClick={undoPick} className="min-h-11 font-bold text-blue-700 hover:underline dark:text-blue-300">Undo</button><a href="#stock-results" className="inline-flex min-h-11 items-center font-bold text-blue-700 hover:underline dark:text-blue-300">Review selection</a></div>
              <details className="mt-3"><summary className="cursor-pointer font-bold">Why are these stocks here?</summary><p className="mt-2 leading-6">These stocks were chosen randomly from the companies that currently meet the criteria for {pickResult.stockFocus}. You can review, add, or remove stocks before creating your bot.</p></details>
              <ul className="mt-4 divide-y divide-slate-200 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
                {(pickResult.selectedCompanies || []).filter((item) => practiceSymbols.includes(item.symbol)).map((item) => { const isKept = (pickResult.keptSymbols || []).includes(item.symbol); return <li key={item.symbol} className="flex items-center justify-between gap-3 p-3"><div className="min-w-0"><span className="font-black">{item.symbol}</span>{item.company_name && <span className="ml-2 truncate text-slate-500 dark:text-slate-400">{item.company_name}</span>}</div><div className="flex shrink-0 flex-wrap justify-end gap-3"><button type="button" aria-pressed={isKept} onClick={() => togglePickedKeep(item.symbol)} className="min-h-11 font-bold text-blue-700 hover:underline dark:text-blue-300">{isKept ? '✓ Kept' : 'Keep'}</button><button type="button" onClick={(event) => openCompanyContext(item, event.currentTarget)} className="min-h-11 font-bold text-blue-700 hover:underline dark:text-blue-300">About</button><button type="button" onClick={() => togglePracticeSymbol(item.symbol)} className="min-h-11 font-bold text-slate-700 hover:underline dark:text-slate-200">Remove</button></div></li>; })}
              </ul>
            </div>}
            {pendingRollCount && <div className="mt-4 rounded-2xl border border-blue-300 bg-white/80 p-4 text-sm dark:border-blue-800 dark:bg-slate-950/50" role="alert"><p className="font-black">Pick another set will replace your current stock selection.</p><p className="mt-1">Your manual selection will be replaced with a new random set from the same Stock Focus.</p><div className="mt-3 flex gap-3"><button type="button" onClick={() => pickForMe(pendingRollCount)} className="min-h-11 rounded-xl bg-blue-700 px-4 py-2 font-bold text-white hover:bg-blue-800">Pick another set</button><button type="button" onClick={() => setPendingRollCount(null)} className="min-h-11 rounded-xl border border-blue-300 px-4 py-2 font-bold text-blue-800 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-200">Keep selection</button></div></div>}
          </section>
        )}

        <section className="mt-8 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
          <div>
            <label htmlFor="stock-search" className="block text-sm font-black">
              {practiceSetup ? 'Search stocks' : 'Search the market'}
            </label>
            <p id="stock-search-guidance" className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {practiceSetup
                ? 'Search by ticker or company name, then select the stock below.'
                : 'Browse verified stocks below. Search also finds older listings, clearly marked when current data is unverified.'}
            </p>
          </div>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <input
              id="stock-search"
              type="text"
              aria-describedby={`stock-search-guidance${searchHint ? ' stock-search-hint' : ''}`}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
                setResultPage(1);
                setSearchHint(null);
                setNotification(null);
              }}
              placeholder={stockSearchPlaceholder({
                practiceSetup,
                stockFocus: activeStockFocus,
              })}
              className="min-h-12 w-full rounded-xl border border-slate-300 bg-transparent px-4 py-3 text-slate-950 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:text-white"
            />
            <button
              type="button"
              onClick={fetchStockData}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-bold text-white transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-60"
              disabled={loading}
            >
              <FaSearch aria-hidden="true" /> {loading ? 'Searching…' : 'Search'}
            </button>
          </div>
          {searchFeedback && <p role="alert" className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-800 dark:bg-red-950/40 dark:text-red-200">{searchFeedback}</p>}
          {searchHint && (
            <p id="stock-search-hint" role="status" className="mt-3 text-sm font-semibold text-amber-800 dark:text-amber-300">
              {stockSearchRecoveryMessage({ ...searchHint, practiceSetup })}
            </p>
          )}

          <details className="group mt-5 border-t border-slate-100 pt-4 dark:border-slate-800">
            <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-bold text-slate-700 hover:text-blue-700 dark:text-slate-300 dark:hover:text-blue-300">
              <FaFilter className="text-blue-600" aria-hidden="true" />
              Choose table columns
              <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs text-blue-800 dark:bg-blue-950 dark:text-blue-300">
                {selectedMetricCount} optional {selectedMetricCount === 1 ? "column" : "columns"}
              </span>
            </summary>
            <div className="mt-4 flex flex-wrap gap-2">
              {METRICS.map((metric) => (
                <button
                  type="button"
                  key={metric}
                  onClick={() => toggleMetric(metric)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                    selectedMetrics[metric]
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-slate-300 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                  }`}
                  disabled={loading}
                  aria-pressed={Boolean(selectedMetrics[metric])}
                >
                  {metricLabel(metric)}
                </button>
              ))}
              {selectedMetricCount > 0 && (
                <button type="button" onClick={() => setSelectedMetrics({})} className="rounded-full px-3 py-1.5 text-xs font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                  Clear columns
                </button>
              )}
            </div>
            {!practiceSetup && <p className="mt-3 text-xs text-slate-500">A field shows “Unavailable” when this publication has no verified value. Not yet published: {NOT_PUBLISHED_METRICS.map(metricLabel).join(', ')}.</p>}
          </details>
        </section>

        {loading ? (
          <section className="mt-6 rounded-3xl border border-slate-200 bg-white py-16 dark:border-slate-800 dark:bg-slate-900" aria-busy="true">
            <LoadingSpinner showLabel label="Loading stock data…" />
          </section>
        ) : screeningUnavailable ? (
          <section className="mt-6 rounded-3xl border border-amber-300 bg-amber-50 px-6 py-12 text-center text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100" role="status">
            <h2 className="text-xl font-black">We&apos;re refreshing our stock data</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6">
              Stock browsing is temporarily paused while we verify the latest market information. We&apos;re working to restore access as soon as the data meets our quality standards.
            </p>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6">Thanks for your patience. Please check back shortly.</p>
            {practiceSetup && <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/bots?step=3#hire" className="rounded-xl bg-amber-700 px-5 py-3 text-sm font-bold text-white hover:bg-amber-800">
                Enter symbols manually
              </Link>
              <Link to="/bots?step=3#hire" className="rounded-xl border border-amber-500 px-5 py-3 text-sm font-bold hover:bg-amber-100 dark:hover:bg-amber-950">
                Back to bot setup
              </Link>
            </div>}
          </section>
        ) : stockData.length > 0 ? (
          <section id="stock-results" className={`mt-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-opacity duration-300 dark:border-slate-800 dark:bg-slate-900 motion-reduce:transition-none ${pickSettling ? 'animate-pulse motion-reduce:animate-none' : ''}`}>
            <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-5 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div>
                <h2 className="text-2xl font-black">Stock results</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {practiceSetup
                    ? stockCandidateSummary({
                      total: totalStocks,
                      visible: visibleStocks.length,
                      stockFocus: activeStockFocus,
                    })
                    : `${visibleStocks.length} of ${stockData.length} loaded stocks shown${selectedMetricCount ? ` · ${selectedMetricCount} extra columns` : ''}`}
                </p>
                {practiceSetup && (
                  <p role="status" aria-live="polite" aria-atomic="true" className="mt-2 text-sm font-black text-blue-700 dark:text-blue-300">
                    Selected: {practiceSymbols.length} {practiceSymbols.length === 1 ? 'stock' : 'stocks'}
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500">Scroll sideways to see selected columns on smaller screens.</p>
            </div>
            <div className="overflow-x-auto" tabIndex="0" aria-label="Scrollable stock results">
              <table className="min-w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  <tr>
                    {preferredOrder.map((key, index) => (
                      <React.Fragment key={key}>
                      <th key={key} scope="col" className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-wide first:pl-6">
                        {key === "logo" ? "Logo" :
                         key === "company_name" ? "Company Name" :
                         key === "symbol" ? "Symbol" :
                         key === "description" ? "Description" :
                         key === "domain" ? "Domain" :
                         key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      </th>
                      {practiceSetup && index === 0 && (
                        <>
                          <th scope="col" className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-wide">Select for bot</th>
                          {!allStocksMode && <th scope="col" className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-wide">Evidence</th>}
                        </>
                      )}
                      </React.Fragment>
                    ))}
                    {otherMetrics.map((metric) => (
                      <th key={metric} scope="col" className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-wide">
                        {metricLabel(metric)}
                      </th>
                    ))}
                    {!practiceSetup && <th scope="col" className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-wide">Current data</th>}
                    <th scope="col" className="whitespace-nowrap px-4 py-3 text-left text-xs font-black uppercase tracking-wide">Stock detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleStocks.map((stock) => {
                    const practiceIncompatible = practiceSetup
                      && stock.practice_bot_eligible === false;
                    return (
                    <tr key={stock.symbol} className={`border-t border-slate-100 transition-colors dark:border-slate-800 ${stock.instrument_id ? 'cursor-pointer hover:bg-blue-50/60 dark:hover:bg-blue-950/20' : ''} ${openingInstrumentId === Number(stock.instrument_id) ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}
                      onClick={(event) => { if (!event.target.closest('button, a, input, summary') && stock.instrument_id) openInstrument(stock); }}
                      onKeyDown={(event) => { if (event.target === event.currentTarget && stock.instrument_id && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); openInstrument(stock); } }}
                      tabIndex={stock.instrument_id ? 0 : undefined}
                      role={stock.instrument_id ? 'link' : undefined}
                      aria-label={stock.instrument_id ? `Explore ${stock.company_name || stock.symbol} stock detail` : undefined}
                      aria-busy={openingInstrumentId === Number(stock.instrument_id)}>
                      {preferredOrder.map((key, index) => (
                        <React.Fragment key={key}>
                        <td key={key} className={`px-4 py-3 text-slate-700 first:pl-6 dark:text-slate-300 ${key === 'company_name' ? 'min-w-64' : 'whitespace-nowrap'}`}>
                          {formatValue(key, stock[key], stock.symbol, stock.data_status)}
                          {key === 'company_name' && stock.data_status === 'LEGACY_UNVERIFIED' && <span className="mt-1 block text-xs font-semibold text-amber-700 dark:text-amber-300">Name from an older listing; not verified today</span>}
                          {key === 'company_name' && stock.data_status === 'LEGACY_UNVERIFIED' && stock.instrument_id && <button type="button" onClick={() => openInstrument(stock)} disabled={openingInstrumentId === Number(stock.instrument_id)} className="mt-1 block min-h-11 text-left text-xs font-bold text-blue-700 underline disabled:opacity-60 dark:text-blue-300">View limited detail →</button>}
                          {key === 'company_name' && openingInstrumentId === Number(stock.instrument_id) && <span className="ml-2 text-xs font-bold text-blue-700 dark:text-blue-300" role="status">Opening stock detail…</span>}
                        </td>
                        {practiceSetup && index === 0 && (
                          <>
                            <td className="px-4 py-3">
                              {practiceSymbols.includes(stock.symbol) ? (
                                <div className="flex items-center gap-2">
                                  <span className={`whitespace-nowrap text-xs font-black ${practiceIncompatible ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}>{practiceIncompatible ? 'Needs replacement' : '✓ Selected'}</span>
                                  <button
                                    type="button"
                                    onClick={() => togglePracticeSymbol(stock.symbol)}
                                    className="min-h-11 whitespace-nowrap rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                    aria-label={`Remove ${stock.company_name || stock.symbol} (${stock.symbol}) from this bot`}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : practiceIncompatible ? (
                                <div className="max-w-xs text-xs leading-5 text-amber-800 dark:text-amber-300">
                                  <span className="font-black">Unavailable for Practice</span>
                                  <span className="mt-1 block">{stock.practice_bot_eligibility_message || 'Choose another compatible stock.'}</span>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => togglePracticeSymbol(stock.symbol)}
                                  disabled={Boolean(searchFeedback)}
                                  className="min-h-11 whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-blue-700"
                                  aria-label={`Select ${stock.company_name || stock.symbol} (${stock.symbol}) for this bot`}
                                >
                                  Select
                                </button>
                              )}
                              {selectionFeedback?.symbol === stock.symbol && <p ref={selectionFeedbackRef} role={selectionFeedback.type === 'warning' ? 'alert' : 'status'} className={`mt-2 max-w-xs text-xs font-semibold leading-5 ${selectionFeedback.type === 'warning' ? 'text-amber-800 dark:text-amber-300' : 'text-emerald-800 dark:text-emerald-300'}`}>{selectionFeedback.message}</p>}
                            </td>
                            {!allStocksMode && <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={(event) => openCompanyContext(stock, event.currentTarget)}
                                aria-label={`About ${stock.company_name || stock.symbol} — View evidence`}
                                className="min-h-11 whitespace-nowrap rounded-lg px-2 py-2 text-xs font-bold text-blue-700 hover:underline dark:text-blue-300"
                              >
                                About
                              </button>
                            </td>}
                          </>
                        )}
                        </React.Fragment>
                      ))}
                      {otherMetrics.map((metric) => (
                        <td
                          key={metric}
                          className={`max-w-sm px-4 py-3 text-slate-700 dark:text-slate-300 ${
                            metric === "description" ? "min-w-80 whitespace-normal" : "whitespace-nowrap"
                          }`}
                        >
                          {formatValue(metric, stock[metric], stock.symbol, stock.data_status)}
                        </td>
                      ))}
                      {!practiceSetup && <td className="max-w-48 px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300">{genericDataLabel(stock.data_status)}{stock.availability && <span className="mt-1 block font-normal">{stock.availability.reason}{lastVerifiedPublicationDate(stock) && ` Last verified ${lastVerifiedPublicationDate(stock)}.`}</span>}</td>}
                      <td className="px-4 py-3">{stock.instrument_id && !['QUALITY_LIMITED', 'EXCLUDED'].includes(stock.data_status) ? <button type="button" onClick={() => openInstrument(stock)} disabled={openingInstrumentId === Number(stock.instrument_id)} className="min-h-11 whitespace-nowrap rounded-xl border border-blue-300 px-3 py-2 text-sm font-bold text-blue-700 hover:bg-blue-50 disabled:cursor-wait disabled:opacity-60 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950/30">{openingInstrumentId === Number(stock.instrument_id) ? 'Opening stock detail…' : stock.data_status === 'LEGACY_UNVERIFIED' ? 'View limited detail' : 'Explore stock'}</button> : <span className="block max-w-48 text-xs leading-5 text-slate-600 dark:text-slate-300">{unavailableDetailReason(stock.data_status)}</span>}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {stockData.length > CLIENT_PAGE_SIZE && (
              <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-100 px-5 py-4 dark:border-slate-800 sm:flex-row sm:px-6">
                <button
                  type="button"
                  onClick={() => setResultPage((current) => Math.max(1, current - 1))}
                  disabled={resultPage === 1}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2 font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 sm:w-auto"
                >
                  Previous
                </button>
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                  Results page {resultPage} of {resultPages}
                </span>
                <button
                  type="button"
                  onClick={() => setResultPage((current) => Math.min(resultPages, current + 1))}
                  disabled={resultPage === resultPages}
                  className="w-full rounded-xl bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
                >
                  Next
                </button>
              </div>
            )}
            {!searchQuery.trim() && totalStocks > 100 && (
              <details className="border-t border-slate-100 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/40 sm:px-6">
                <summary className="cursor-pointer text-sm font-bold text-slate-600 dark:text-slate-300">More stocks from the market universe</summary>
                <p className="mt-2 text-xs leading-5 text-slate-500">For performance, Pinngro loads 100 stocks at a time. The Results controls above move through the current group; these controls load a different group.</p>
                <div className="mt-3 flex flex-col items-center justify-between gap-3 sm:flex-row">
                  <button type="button" onClick={() => handlePageChange(page - 1)} disabled={page === 1} className="w-full rounded-xl border border-slate-300 px-4 py-2 font-bold disabled:opacity-40 dark:border-slate-700 sm:w-auto">Previous group</button>
                  <span className="text-sm font-semibold">Market group {page} of {Math.ceil(totalStocks / 100)}</span>
                  <button type="button" onClick={() => handlePageChange(page + 1)} disabled={page * 100 >= totalStocks} className="w-full rounded-xl bg-blue-600 px-4 py-2 font-bold text-white disabled:opacity-40 sm:w-auto">Load next group</button>
                </div>
              </details>
            )}
          </section>
        ) : (
          <section role="status" aria-live="polite" className="mt-6 rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center dark:border-slate-700 dark:bg-slate-900">
            <FaSearch className="mx-auto text-3xl text-slate-400" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-black">
              {stockEmptyStateTitle({ practiceSetup, stockFocus: activeStockFocus })}
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">
              {searchQuery.trim()
                ? stockEmptySearchMessage({ query: searchQuery, practiceSetup, stockFocus: activeStockFocus })
                : stockEmptyStateDetail({ practiceSetup, stockFocus: activeStockFocus })}
            </p>
            {searchQuery && (
              <button type="button" onClick={() => { setSearchQuery(""); setPage(1); }} className="mt-5 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700">
                {stockClearSearchLabel({ practiceSetup, stockFocus: activeStockFocus })}
              </button>
            )}
          </section>
        )}
        {companyContextStock && (
          <StockFocusCompanyDrawer
            stock={companyContextStock}
            stockFocus={practiceDraft.preset}
            lineage={screeningGeneration}
            axiosInstance={axiosInstance}
            onClose={closeCompanyContext}
          />
        )}
      </main>
    </div>
  );
};

export default StockExplorer;
